import { css } from '@emotion/react';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, type ReactNode,
} from 'react';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { Inline } from '~/_shared/baseComponents/layout/inline.component';
import { FloatingBoxComponent } from '~/_shared/components/floatingBox/floatingBox.component';
import type { Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';

const warningRowStyle = (theme: Theme) => css({
  alignItems: 'center',
  color: theme.textColors.warning,
  display: 'flex',
  width: '100%',
});

const warningIconStyle = css({
  flexShrink: 0,
  fontSize: 'inherit',
});

const stepRowStyle = css({
  alignItems: 'baseline',
  display: 'flex',
  width: '100%',
});

const stepLabelStyle = css({
  flexShrink: 0,
  fontWeight: 700,
  whiteSpace: 'nowrap',
});

type ToolInstructionsBoxProps = Readonly<{
  className?: string;
  heading?: string;
  steps: ReadonlyArray<ReactNode>;
  note?: string;

  onClose?: () => void;
  warning?: string;
}>;

export const ToolInstructionsBoxComponent: FC<ToolInstructionsBoxProps> = ({
  className,
  heading,
  onClose,
  steps,
  warning,
  note,
}) => {
  const [t] = useTranslation();

  return (
    <FloatingBoxComponent
      css={className}
      gap={12}
      heading={heading}
      isOpen
      onClose={onClose}
    >
      {!!warning && (
        <Inline
          css={warningRowStyle}
          gap={8}
          wrap="nowrap"
        >
          <FontAwesomeIcon css={warningIconStyle} icon={faExclamationTriangle} />
          <span>{warning}</span>
        </Inline>
      )}

      {steps.map((step, index) => (
        <Inline
          css={stepRowStyle}
          gap={8}
          key={index}
          wrap="nowrap"
        >
          <span css={stepLabelStyle}>
            {t('Step {{step}}', { step: index + 1 })}:
          </span>
          <span>{step}</span>
        </Inline>
      ))}
      {!!note && (
        <Inline
          css={stepRowStyle}
          gap={8}
          wrap="nowrap"
        >
          <span css={stepLabelStyle}>
            {t('Note:')}
          </span>
          <span>{note}</span>
        </Inline>
      )}
    </FloatingBoxComponent>
  );
};
